import { useCallback, useMemo, useEffect } from 'react';
import { useLocation } from 'react-router-dom';
import { useIsAuthenticated } from 'react-auth-kit';
import { useEventTracking as useEventTrackingLib } from '@DigitalZoneApp/event-tracking/front';

import { EventTrackingService } from '../lib/analytics';
import { useAuthCode } from '../context/AuthCodeContext/AuthCodeContext';
import { isSuperQi } from '../utils/miniAppPlatform';
import type { Source } from '../lib/analytics/types';

interface JwtPayload {
  userId?: string;
  sub?: string;
  preferred_username?: string;
  platform?: string;
}

interface UserInfo {
  userId: string;
  phone?: string;
}

let lastIdentifiedUserId: string | null = null;

const pathnameToSource = (pathname: string): Source => {
  const normalizedPath = pathname.replace(/^\/[a-z]{2}(-[A-Z]{2})?(\/|$)/, '/');

  const firstSegment = normalizedPath.replace(/^\//, '').split('/')[0] || '';

  switch (firstSegment) {
    case '':
      return 'home_page';
    case 'concert':
      return 'concert_page';
    case 'event':
      return 'event_page';
    case 'matches':
      return 'matches_page';
    case 'concerts':
      return 'concerts_page';
    case 'workshops':
      return 'workshops_page';
    case 'checkout':
      return 'checkout_page';
    case 'summary':
      return 'order_summary_page';
    case 'profile':
      return 'profile_page';
    case 'history':
      return 'history_page';
    case 'about-us':
      return 'about_us_page';
    case 'ticket-generator':
      return 'ticket_generator_page';
    case 'terms-of-use':
      return 'terms_of_use_page';
    case 'welcome':
      return 'welcome_page';
    case 'international-events':
    case 'international-event':
      return 'international_events_page';
    default:
      return 'navigation';
  }
};

const decodeJwtPayload = (token: string): JwtPayload | null => {
  try {
    const base64Url = token.split('.')[1];
    if (!base64Url) return null;

    const base64 = base64Url.replace(/-/g, '+').replace(/_/g, '/');
    const jsonPayload = decodeURIComponent(
      atob(base64)
        .split('')
        .map((c) => '%' + ('00' + c.charCodeAt(0).toString(16)).slice(-2))
        .join(''),
    );

    return JSON.parse(jsonPayload);
  } catch {
    return null;
  }
};

export const useEventTracking = () => {
  const { track, ready, identify } = useEventTrackingLib();
  const location = useLocation();
  const { userToken, authCode } = useAuthCode();
  const isAuthenticated = useIsAuthenticated();

  const getPathname = useCallback(() => location.pathname, [location.pathname]);

  const getAppSource = useCallback(() => {
    if (isSuperQi() && authCode) {
      return 'mini_app';
    }
    if (userToken) {
      return 'qi_services';
    }
    return 'web';
  }, [authCode, userToken]);

  const getUserInfo = useCallback((): UserInfo | undefined => {
    const accessToken = localStorage.getItem('_auth');
    if (!accessToken) return undefined;

    const payload = decodeJwtPayload(accessToken);
    if (payload?.userId) {
      return {
        userId: payload.userId,
        phone: payload.preferred_username,
      };
    }

    return undefined;
  }, []);

  const getUserId = useCallback(() => {
    return getUserInfo()?.userId;
  }, [getUserInfo]);

  const getSource = useCallback((): Source => {
    return pathnameToSource(location.pathname);
  }, [location.pathname]);

  const isUserAuthenticated = isAuthenticated();

  useEffect(() => {
    if (!ready || !isUserAuthenticated) {
      if (!isUserAuthenticated) {
        lastIdentifiedUserId = null;
      }
      return;
    }

    const userInfo = getUserInfo();
    if (!userInfo) return;

    if (lastIdentifiedUserId !== userInfo.userId) {
      identify(userInfo.userId, { phone: userInfo.phone });
      lastIdentifiedUserId = userInfo.userId;
    }
  }, [ready, isUserAuthenticated, getUserInfo, identify]);

  const eventTracking = useMemo(
    () => new EventTrackingService(track, getPathname, getAppSource, getUserId, getSource),
    [track, getPathname, getAppSource, getUserId, getSource],
  );

  return { eventTracking, track, ready, identify };
};
